import { ReactElement, useEffect, useMemo } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import AgencyDashboard from "./AgencyDashboard/AgencyDashboard";
import PaymentSchedule from "./PaymentSchedule/PaymentSchedule";
import DocumentListing from "./DocumentListing/DocumentListing";
import NewTask from "./NewTask/NewTask";
import PolicyListing from "./PolicyListing/PolicyListing";
import CustomerListing from "./CustomerListing/CustomerListing";
import CommissionStatements from "./CommissionStatements/CommissionStatements";
import CustomerDetails from "./CustomerDetails/CustomerDetails";
import AgencyHeader from "../common/AgencyHeader/AgencyHeader";
import AgencyFooter from "../common/AgentFooter/AgentFooter";
import ConfigureDashboardResponsive from "./ConfigureDashBoardResponsive/ConfigureDashboardResponsive";
import ClaimTrackingResponsive from "./ClaimTrackingResponsive/ClaimTrackingResponsive";
import SendRemainderResponsive from "./SendRemainderResponsive/SendRemainderResponsive";
import ShareMarketingCollateralResponsive from "./ShareMarketingCollateralResponsive/ShareMarketingCollateralResponsive";
import MyTasks from "./MyTasks/MyTasks";
import MonthlyCommissionStatements from "./MonthlyCommissionStatements/MonthlyCommissionStatements";
import DocumentProperties from "./DocumentProperties/DocumentProperties";
import { documentPropertiesData } from "../constants/DocumentPropertiesData";
import DocumenManagementDetails from "./DocumenManagementDetails/DocumenManagementDetails";
import PolicyDetails from "../customers/PolicyDetails/PolicyDetails";
import { getCookie } from "../utils/Utility";
import AgentLogin from "./Login/AgentLogin";
import Hierarchy from "./Hierarchy/Hierarchy";
import UnderWriter from "./UnderWriter/UnderWriter";

const AgentsRoutes = () => {
  const isSmallScreen = useMediaQuery("(max-width:1023px)");
  const navigate = useNavigate();
  const location = useLocation();

  const pathArray = location.pathname.split("/");
  const producerTypeCd = getCookie("producerTypeCd");

  const authenticated = getCookie("authenticated");
  const roleCode = getCookie("roleCode");

  useEffect(() => {
    if (!authenticated || roleCode !== "Producer") {
      navigate("/agent/login");
    }
  }, [authenticated, roleCode, navigate]);

  const header = useMemo(
    () => (authenticated && roleCode === "Producer" ? <AgencyHeader /> : <></>),
    [authenticated, roleCode]
  );

  const footer = useMemo(
    () => (authenticated && roleCode === "Producer" ? <AgencyFooter /> : <></>),
    [authenticated, roleCode]
  );

  useEffect(() => {
    const pathArray = location.pathname.split("/");
    if (
      authenticated &&
      roleCode === "Producer" &&
      producerTypeCd === "Producer" &&
      pathArray[2] === "hierarchy"
    ) {
      navigate("/");
    }
  }, [producerTypeCd, authenticated, roleCode, location]);

  const routes: ReactElement = useMemo(
    () =>
      authenticated && roleCode === "Producer" ? (
        <>
          <Route path="/dashboard" element={<AgencyDashboard />} />
          <Route path="/mytasks" element={<MyTasks />} />
          <Route path="/hierarchy" element={<Hierarchy />} />
          <Route path="/paymentSchedule" element={<PaymentSchedule />} />
          <Route path="/documentListing" element={<DocumentListing />} />
          <Route path="/policyListing" element={<PolicyListing />} />
          {/* <Route path="/policy-details" element={<PolicyDetails />} /> */}
          <Route
            path="/policyDetails/:policyId/:carrier/:system/:lobCode/:customerId"
            element={<PolicyDetails />}
          />
          <Route path="/customerListing" element={<CustomerListing />} />
          <Route path="/newTask" element={isSmallScreen && <NewTask />} />
          <Route
            path="/commissionStatements"
            element={<CommissionStatements />}
          />
          <Route
            path="/monthlyStatement"
            element={<MonthlyCommissionStatements />}
          />
          <Route path="/config" element={<ConfigureDashboardResponsive />} />
          <Route
            path="/customerDetails/:customerId"
            element={<CustomerDetails />}
          />
          <Route
            path="/claimTracking"
            element={isSmallScreen && <ClaimTrackingResponsive />}
          />
          <Route
            path="/market"
            element={isSmallScreen && <ShareMarketingCollateralResponsive />}
          />
          <Route
            path="/underwriter"
            element={<UnderWriter />}
          />
          <Route
            path="/properties"
            element={
              <DocumentProperties
                documentPropertiesData={documentPropertiesData}
              />
            }
          />
          <Route
            path="/documentDetails"
            element={<DocumenManagementDetails />}
          />
          <Route
            path="/remainder"
            element={isSmallScreen && <SendRemainderResponsive />}
          />
        </>
      ) : (
        <>
          <Route index element={<AgentLogin />} />
          <Route path="/login" element={<AgentLogin />} />
        </>
      ),
    [authenticated, roleCode, isSmallScreen]
  );

  return (
    <Box
      sx={{
        background: "linear-gradient(180deg, #fbfdfc 0%, #e5f6fe 100%)",
        position: "relative",
      }}
    >
      {header}
      <Box
        sx={{
          marginY:
            pathArray[1] === "agent"
              ? pathArray[2] === "login"
                ? 0
                : "3rem"
              : 0,
          minHeight: "calc(100vh - 100px)",
        }}
      >
        <Routes>{routes}</Routes>
      </Box>
      {footer}
    </Box>
  );
};

export default AgentsRoutes;
